<template>
    <main class="flex-1 relative overflow-y-auto py-6 focus:outline-none" tabindex="0">
        <div class="max-w-7xl mx-auto sm:px-6 lg:px-8 px-5">
            <div class="md:flex md:items-center md:justify-between">
                <div class="flex-1 min-w-0">
                    <h1 class="py-0.5 text-2xl font-semibold text-gray-900">{{ $t('Canned replies') }}</h1>
                </div>
                <div class="mt-4 flex md:mt-0 md:ml-4">
                    <router-link
                        class="btn btn-gray shadow-sm rounded"
                        to="/workspace/admin/settings/canned-replies/new"
                    >
                        {{ $t('Create canned reply') }}
                    </router-link>
                </div>
            </div>
        </div>
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div class="my-6 bg-white shadow overflow-hidden sm:rounded">
                <loading :status="loading"/>
                <div class="bg-white px-4 py-3 flex items-center justify-between border-b border-gray-200 sm:px-6">
                    <div>
                        <label class="sr-only" for="search">{{ $t('Search') }}</label>
                        <div class="flex rounded shadow-sm">
                            <div class="relative flex-grow focus-within:z-10">
                                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <svg-vue class="h-5 w-5 text-gray-400" icon="font-awesome.search-regular"></svg-vue>
                                </div>
                                <input
                                    id="search"
                                    v-model.lazy="filters.search"
                                    :placeholder="$t('Search')"
                                    class="form-input block w-full rounded-none rounded-l-md pl-10 text-sm transition ease-in-out duration-150"
                                    @change="getCannedReplies"
                                >
                            </div>
                            <div class="relative inline-flex shadow-sm rounded-r-md">
                                <button
                                    class="relative -ml-px inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-100 active:text-gray-700"
                                    type="button"
                                    @click="changeSort"
                                >
                                    <svg-vue
                                        v-show="sort.order === 'asc'"
                                        class="h-5 w-5 text-gray-400"
                                        icon="font-awesome.sort-amount-down-alt-regular"
                                    ></svg-vue>
                                    <svg-vue
                                        v-show="sort.order === 'desc'"
                                        class="h-5 w-5 text-gray-400"
                                        icon="font-awesome.sort-amount-up-alt-regular"
                                    ></svg-vue>
                                    <span class="ml-2">{{ $t('Sort') }}</span>
                                </button>
                                <select
                                    id="sort.column"
                                    v-model="sort.column"
                                    aria-label="Sort by"
                                    class="-ml-px block form-select w-full pl-3 pr-9 py-2 rounded-l-none rounded-r-md border border-gray-300 bg-white text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150"
                                    @change="changeSort"
                                >
                                    <option value="name">{{ $t('Name') }}</option>
                                    <option value="shared">{{ $t('Shared') }}</option>
                                    <option value="user_id">{{ $t('User') }}</option>
                                    <option value="created_at">{{ $t('Created at') }}</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
                <template v-if="cannedReplyList.length > 0">
                    <ul>
                        <template v-for="(cannedReply, index) in cannedReplyList">
                            <li :class="{'border-t border-gray-200': index !== 0}">
                                <router-link
                                    :to="cannedReplyEditPath(cannedReply.id)"
                                    class="block hover:bg-gray-100 focus:outline-none focus:bg-gray-100 transition duration-150 ease-in-out"
                                >
                                    <div class="flex items-center px-4 py-4 sm:px-6">
                                        <div class="min-w-0 flex-1 flex items-center">
                                            <div class="min-w-0 flex-1 r-4 lg:grid lg:grid-cols-2 lg:gap-4">
                                                <div>
                                                    <div class="text-sm font-medium leading-5 text-gray-800 truncate">{{ cannedReply.name }}</div>
                                                    <div
                                                        v-show="cannedReply.shared"
                                                        class="mt-2 flex items-center text-sm leading-5 text-gray-500"
                                                    >
                                                        <svg-vue class="flex-shrink-0 mr-1.5 h-4 w-4 text-green-400" icon="font-awesome.share-alt-solid"></svg-vue>
                                                        {{ $t('The canned reply is shared') }}
                                                    </div>
                                                    <div
                                                        v-show="!cannedReply.shared"
                                                        class="mt-2 flex items-center text-sm leading-5 text-red-500"
                                                    >
                                                        <svg-vue class="flex-shrink-0 mr-1.5 h-4 w-4 text-red-400" icon="font-awesome.share-alt-solid"></svg-vue>
                                                        {{ $t('The canned reply is not shared') }}
                                                    </div>
                                                </div>
                                                <div class="hidden lg:block">
                                                    <div class="flex justify-start">
                                                        <div class="flex-none">
                                                            <img :alt="$t('Avatar')" :src="cannedReply.user.avatar !== 'gravatar' ? cannedReply.user.avatar : cannedReply.user.gravatar" class="h-10 w-10 rounded-full">
                                                        </div>
                                                        <div class="mx-4">
                                                            <div class="text-sm font-medium text-gray-800 truncate">{{ cannedReply.user.name }}</div>
                                                            <div class="text-sm text-gray-500 truncate">{{ cannedReply.user.email }}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <svg-vue class="h-5 w-5 text-gray-400" icon="font-awesome.angle-right-regular"></svg-vue>
                                        </div>
                                    </div>
                                </router-link>
                            </li>
                        </template>
                    </ul>
                </template>
                <template v-else-if="!loading">
                    <empty-state />
                </template>
                <list-footer-pagination
                    :start="(pagination.perPage * pagination.currentPage) - pagination.perPage + 1"
                    :end="pagination.perPage * pagination.currentPage <= pagination.total ? pagination.perPage * pagination.currentPage : pagination.total"
                    :total="pagination.total"
                    :current-page="pagination.currentPage"
                    :total-pages="pagination.totalPages"
                    @prev="changePage(pagination.currentPage - 1)"
                    @next="changePage(pagination.currentPage + 1)"
                />
            </div>
        </div>
    </main>
</template>

<script>
import {
    queryNumber,
    queryString,
    queryWithoutKeys,
    routeWithReturnTo,
    sameRouteQuery,
    setQueryValue,
} from "@/utils/list-route-state";

export default {
    name: "list",
    metaInfo() {
        return {
            title: this.$i18n.t('Canned replies')
        }
    },
    data() {
        const routeQuery = this.$route && this.$route.query ? this.$route.query : {};
        const allowedSortColumns = ['name', 'shared', 'user_id', 'created_at'];
        const initialSortColumn = queryString(routeQuery, 'list_sort_column', 'created_at');
        const initialSortOrder = queryString(routeQuery, 'list_sort_order', 'asc') === 'desc' ? 'desc' : 'asc';

        return {
            page: queryNumber(routeQuery, 'list_page', 1),
            filters: {
                search: queryString(routeQuery, 'list_search', null),
            },
            sort: {
                order: initialSortOrder,
                column: allowedSortColumns.includes(initialSortColumn) ? initialSortColumn : 'created_at',
            },
            perPage: queryNumber(routeQuery, 'list_per_page', 10),
            loading: true,
            pagination: {
                currentPage: 0,
                perPage: 0,
                total: 0,
                totalPages: 0
            },
            cannedReplyList: [],
        }
    },
    mounted() {
        this.getCannedReplies();
    },
    methods: {
        changePage(page) {
            const self = this;
            if ((page > 0) && (page <= self.pagination.totalPages) && (page !== self.page)) {
                self.page = page;
                self.getCannedReplies();
            }
        },
        changeSort() {
            const self = this;
            if (self.sort.order === 'asc') {
                self.sort.order = 'desc';
            } else if (self.sort.order === 'desc') {
                self.sort.order = 'asc';
            }
            self.getCannedReplies();
        },
        cannedReplyListPath() {
            return String(this.$route && this.$route.path ? this.$route.path : '').startsWith('/workspace/admin/settings/canned-replies')
                ? '/workspace/admin/settings/canned-replies'
                : '/workspace/canned-replies';
        },
        cannedReplyEditPath(cannedReplyId) {
            const listPath = this.cannedReplyListPath();
            return routeWithReturnTo(listPath + '/' + cannedReplyId + '/edit', this.$route, listPath);
        },
        getCannedReplies() {
            const self = this;
            self.syncCannedRepliesListRouteState();
            self.loading = true;
            axios.get('api/workspace/canned-replies', {
                params: {
                    page: self.page,
                    sort: self.sort,
                    perPage: self.perPage,
                    search: self.filters.search
                }
            }).then(function (response) {
                self.cannedReplyList = response.data.items;
                self.pagination = response.data.pagination;
                if (self.pagination.totalPages < self.pagination.currentPage) {
                    self.page = self.pagination.totalPages;
                    self.getCannedReplies();
                } else {
                    self.loading = false;
                }
            }).catch(function () {
                self.loading = false;
            });
        },
        syncCannedRepliesListRouteState() {
            if (!this.$router || !this.$route) {
                return;
            }

            const removeKeys = [
                'list_page',
                'list_per_page',
                'list_sort_column',
                'list_sort_order',
                'list_search',
            ];

            let nextQuery = queryWithoutKeys(this.$route.query || {}, removeKeys);
            setQueryValue(nextQuery, 'list_page', Number(this.page || 1), 1);
            setQueryValue(nextQuery, 'list_per_page', Number(this.perPage || 10), 10);
            setQueryValue(nextQuery, 'list_sort_column', this.sort.column, 'created_at');
            setQueryValue(nextQuery, 'list_sort_order', this.sort.order, 'asc');
            setQueryValue(nextQuery, 'list_search', this.filters.search, '');

            if (!sameRouteQuery(this.$route.query || {}, nextQuery)) {
                this.$router.replace({path: this.$route.path, query: nextQuery}).catch(() => {});
            }
        }
    }
}
</script>
